import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight } from "lucide-react";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import DemoRequestModal from "../components/DemoRequestModal";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const faqCategories = [
  {
    title: "General",
    faqs: [
      {
        question: "What is Praqio?",
        answer: "Praqio is a comprehensive credentialing workflow platform designed for healthcare practices. It helps you manage provider credentials, automate renewal reminders, store documents securely, and keep all your practice data organized in one place. Praqio is a product of Capital Billing Services, Inc."
      },
      {
        question: "Who is Praqio designed for?",
        answer: "Praqio is built for healthcare credentialing teams, practice administrators, billing managers, billing companies, and credentialing companies. It works for organizations of all sizes—from single-provider offices to large multi-location health systems."
      },
      {
        question: "How long does implementation take?",
        answer: "Most practices are up and running within 1-2 weeks. Our team helps you import existing data, set up your workflows, and train your staff. Enterprise implementations may take 3-4 weeks depending on complexity."
      }
    ]
  },
  {
    title: "Features & Functionality",
    faqs: [
      {
        question: "Can I import data from my existing systems?",
        answer: "Yes! Praqio supports data import from CSV and JSON formats. We also offer direct integration with CAQH for credentialing data. Our team can help migrate your existing documents and provider information."
      },
      {
        question: "How do automated reminders work?",
        answer: "You can configure reminders for any credential expiration—licenses, DEA, malpractice, certifications, etc. Reminders are sent via email at intervals you choose (e.g., 90, 60, 30 days before expiration). You can also set up daily digest emails and calendar integrations."
      },
      {
        question: "Does Praqio support e-signatures?",
        answer: "Yes! Our Professional and Enterprise plans include e-signature functionality. You can send documents for signature via email, create reusable templates with drag-and-drop fields, and even bulk send via CSV. Each signed document includes a Certificate of Completion."
      }
    ]
  },
  {
    title: "Security & Compliance",
    faqs: [
      {
        question: "Is Praqio HIPAA compliant?",
        answer: "Yes, Praqio is fully HIPAA compliant. We use AES-GCM encryption for all sensitive data, maintain complete audit logs, implement role-based access control, and offer Business Associate Agreements (BAA) for all customers."
      },
      {
        question: "How is my data protected?",
        answer: "We employ multiple layers of security: AES-GCM encryption for data at rest, TLS 1.3 for data in transit, row-level security on all database tables, automatic session timeout, and 24/7 security monitoring. We're also SOC 2 Type II certified."
      }
    ]
  },
  {
    title: "Pricing & Billing",
    faqs: [
      {
        question: "Is there a free trial?",
        answer: "Yes! We offer a 14-day free trial with full access to Professional plan features. No credit card required to start."
      },
      {
        question: "Can I change plans later?",
        answer: "Yes, you can upgrade or downgrade your plan at any time. When you upgrade, you'll get immediate access to new features. Downgrades take effect at the start of your next billing cycle."
      },
      {
        question: "Do you offer discounts for annual billing?",
        answer: "Yes, we offer a 20% discount when you pay annually. Contact our sales team for details on annual plans and enterprise pricing."
      }
    ]
  }
];

export default function FAQ() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <>
      <DemoRequestModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />

      {/* Hero */}
      <section className="pt-28 pb-8 sm:pt-32 sm:pb-10 md:pt-36 md:pb-12 px-4 sm:px-6" data-testid="faq-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              FAQ
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#1A362D] font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg text-[#5C6C65] max-w-xl mx-auto"
            >
              Find answers to common questions about Praqio's features, 
              security, pricing, and more.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-8 sm:py-10 md:py-12 px-4 sm:px-6" data-testid="faq-content">
        <div className="max-w-2xl mx-auto">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-10"
            >
              <h2 
                className="text-xl font-medium text-[#1A362D] mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {category.title}
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {category.faqs.map((faq, faqIndex) => (
                  <AccordionItem 
                    key={faqIndex} 
                    value={`${categoryIndex}-${faqIndex}`}
                    className="bg-white rounded-xl px-5 border border-[#E2E8D8]"
                  >
                    <AccordionTrigger className="text-left text-[#1A362D] font-medium py-4 hover:no-underline text-sm">
                      <div className="flex items-center gap-3">
                        <HelpCircle size={18} className="text-[#C87961] flex-shrink-0" />
                        <span>{faq.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[#5C6C65] pb-4 pl-8">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-3xl tracking-tight text-[#1A362D] font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Still Have Questions?
          </h2>
          <p className="text-[#5C6C65] mb-6">
            Our team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button className="btn-pill btn-primary px-6 py-5 w-full sm:w-auto">
                Contact Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="btn-pill btn-outline px-6 py-5"
              onClick={() => setDemoModalOpen(true)}
            >
              Request Demo
            </Button>
          </div>
          <p className="text-xs text-[#8A9A86] mt-6">
            A product of <span className="text-[#1A362D] font-medium">Capital Billing Services, Inc.</span>
          </p>
        </div>
      </section>
    </>
  );
}
